import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api, { formatApiError, formatNaira } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function CheckoutPage() {
  const { items, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    city: "",
    state: "",
    country: "Nigeria",
  });

  if (items.length === 0) {
    return <div className="max-w-3xl mx-auto p-16 text-center" data-testid="checkout-empty">
      <p className="text-[#6A747D]">Your cart is empty.</p>
    </div>;
  }

  const shipping = cartTotal > 5000 ? 0 : 500;
  const total = cartTotal + shipping;

  const placeOrder = async () => {
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        shipping_address: address,
        payment_method: "paystack",
      });

      // Initialize Paystack
      try {
        const { data: init } = await api.post("/payments/paystack/initialize", {
          order_id: order.id,
          callback_url: `${window.location.origin}/payment/callback`,
        });
        clearCart();
        window.location.href = init.authorization_url;
      } catch (e) {
        const msg = formatApiError(e);
        if (e.response?.status === 503) {
          // Paystack not configured – fallback to mock confirmation
          toast(`Paystack not configured. Your order ${order.id} has been placed (test mode).`, { duration: 5000 });
          clearCart();
          navigate(`/receipt/${order.id}`);
        } else {
          toast.error(msg);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <p className="label-eyebrow mb-3">Step {step} of 2</p>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mb-12">
        {step === 1 ? "Shipping details" : "Payment"}
      </h1>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          {step === 1 ? (
            <form
              onSubmit={(e) => { e.preventDefault(); setStep(2); }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-5"
              data-testid="shipping-form"
            >
              {[
                ["full_name", "Full name", "text", "sm:col-span-2"],
                ["email", "Email", "email", ""],
                ["phone", "Phone", "tel", ""],
                ["address", "Address", "text", "sm:col-span-2"],
                ["city", "City", "text", ""],
                ["state", "State", "text", ""],
              ].map(([k, label, type, col]) => (
                <div key={k} className={col}>
                  <label className="label-eyebrow block mb-2">{label}</label>
                  <input
                    required
                    type={type}
                    value={address[k]}
                    onChange={(e) => setAddress({ ...address, [k]: e.target.value })}
                    className="aj-input"
                    data-testid={`shipping-${k}`}
                  />
                </div>
              ))}
              <div className="sm:col-span-2 flex justify-end mt-4">
                <button type="submit" className="btn-gold" data-testid="continue-to-payment-btn">Continue to Payment</button>
              </div>
            </form>
          ) : (
            <div data-testid="payment-step">
              <div className="border border-[#E8E2D9] p-6 mb-6">
                <h3 className="font-serif text-xl mb-2">Paystack</h3>
                <p className="text-sm text-[#6A747D]">You'll be redirected to Paystack's secure checkout to complete your payment with card, bank transfer, or USSD.</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="btn-outline" data-testid="back-to-shipping-btn">Back</button>
                <button onClick={placeOrder} disabled={loading} className="btn-gold" data-testid="place-order-btn">
                  {loading ? "Processing…" : `Pay ${formatNaira(total)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        <aside className="bg-[#F3EFEA] p-8 h-fit" data-testid="checkout-summary">
          <h2 className="font-serif text-2xl mb-6">Order</h2>
          <div className="space-y-3 mb-6">
            {items.map((it) => (
              <div key={it.product.id} className="flex justify-between text-sm gap-4">
                <span className="flex-1">{it.product.name} × {it.quantity}</span>
                <span>{formatNaira(it.product.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E8E2D9] pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatNaira(cartTotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatNaira(shipping)}</span></div>
            <div className="flex justify-between font-serif text-xl pt-3 border-t border-[#E8E2D9] mt-3"><span>Total</span><span data-testid="checkout-total">{formatNaira(total)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
